import { Box, Flex, Text } from '@chakra-ui/react';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import CounterBadge from '../CounterBadge';
import NavTag from './NavTag';

export interface SubNavItem {
  label: string;
  to: string;
  badge?: number;
  /** Match the pathname exactly instead of by prefix (e.g. /affiliates vs /affiliates/invitations). */
  exact?: boolean;
  /**
   * A destination the brand can see but not reach yet. Rendered muted and
   * inert, never as a link: a row that looks clickable and answers nothing is
   * worse than one that says plainly it is not ready.
   */
  disabled?: boolean;
  /** Word badge, e.g. the "coming soon" marker on a disabled row. */
  tag?: string;
}

interface NavSubLinkProps extends SubNavItem {
  /** Position in the group, for the staggered reveal. */
  index: number;
  /** Whether the parent group is expanded (drives the cascade). */
  isOpen: boolean;
  /** Skip translate transforms when the user prefers reduced motion. */
  reduceMotion: boolean;
}

const NavSubLink: React.FC<NavSubLinkProps> = ({
  label,
  to,
  badge,
  exact,
  disabled,
  tag,
  index,
  isOpen,
  reduceMotion,
}) => {
  const location = useLocation();
  const isActive =
    !disabled && (exact ? location.pathname === to : location.pathname.startsWith(to));

  // One shape for both rows: a disabled destination must sit exactly where a
  // reachable one would, or the menu reads as two lists rather than one.
  const rowStyle = {
    align: 'center' as const,
    gap: '8px',
    h: '30px',
    px: '10px',
    rounded: '8px',
    fontFamily: 'heading',
    fontSize: '13.5px',
    letterSpacing: '-0.005em',
    fontWeight: isActive ? 600 : 500,
    bg: isActive ? 'navDark.hoverBg' : 'transparent',
    transition: 'background-color .12s, color .12s',
  };

  return (
    <Box
      style={{
        opacity: isOpen ? 1 : 0,
        transform: isOpen || reduceMotion ? 'none' : 'translateY(-6px)',
        transition: reduceMotion
          ? 'opacity .15s ease'
          : 'opacity .22s ease, transform .26s cubic-bezier(.22,1,.36,1)',
        transitionDelay: isOpen ? `${60 + index * 45}ms` : '0ms',
      }}
    >
      {disabled ? (
        <Flex {...rowStyle} color="navDark.sectionLabel" cursor="default" aria-disabled>
          <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            {label}
          </Text>
          {tag ? <NavTag tone="muted">{tag}</NavTag> : null}
        </Flex>
      ) : (
        <Link to={to} style={{ display: 'block' }}>
          <Flex
            {...rowStyle}
            color={isActive ? 'navDark.textStrong' : 'navDark.subText'}
            _hover={{
              color: 'navDark.textStrong',
              bg: isActive ? 'navDark.hoverBg' : 'navDark.subHoverBg',
            }}
          >
            <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {label}
            </Text>
            {badge ? <CounterBadge count={badge} /> : null}
            {tag ? <NavTag>{tag}</NavTag> : null}
          </Flex>
        </Link>
      )}
    </Box>
  );
};

export default NavSubLink;
